import { capitalizeName } from 'helpers/stringHelpers';
import cloneDeep from 'lodash/cloneDeep';
import { useFetchWgUsersList } from 'pages/WellGuardian/WIR/hooks';
import { NOT_APPLICABLE } from 'pages/WellGuardian/WIR/wir.constants';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { useCountryState, useWIRUsers } from '../../../../../redux/WellGuardian/selectors';
import userApiClient from 'services/networking/userApiClient';
import wellGuardianApiClient from 'services/networking/wellGuardianApiClient';
import {
  AssetType,
  Profiles,
  UnitSystems,
  UserDTO,
  WIRTransverseRoles,
  WIRUser,
} from 'types/wellGuardian';

import { UserMode } from '../userRoleSettingsModal.constants';

const WIR_USERS_PAGE_SIZE = 10;

const ALL_WIR = 'ALL';
const WG_USER_EXISTS_ERROR_MESSAGE = 'User Already exists in WG users';

export const useUserRoleSettingsModalData = () => {
  const intl = useIntl();
  const [page, setPage] = useState<number>(1);
  const [openUserRoleSettingsModal, setOpenUserRoleSettingsModal] = useState<boolean>(false);
  const [wirUsersForPage, setWirUsersForPage] = useState<WIRUser[]>([]);
  const [openUserDetailsModal, setOpenUserDetailsModal] = useState<boolean>(false);
  const [assetTypeFilter, setAssetTypeFilter] = useState<string>(ALL_WIR);
  const [roleTypeFilter, setRoleTypeFilter] = useState<string>(NOT_APPLICABLE);
  const [searchTypeFilter, setSearchTypeFilter] = useState<string>(NOT_APPLICABLE);
  const [openRemoveUserConfirmationModal, setOpenRemoveUserConfirmationModal] =
    useState<boolean>(false);
  const [user, setUser] = useState<WIRUser>({});
  // const [createUserBody, setCreateUserBody] = useState<Partial<UserDTO>>({});
  const [mode, setMode] = useState<UserMode>();
  const countryData = useCountryState();
  const [excelData, setExcelData] = useState<any>(null);
  const [finalExcelData, setFinalExcelData] = useState<any>({});
  const [multiUser, setMultiUser] = useState<boolean>(false);
  const [userData, setUserData] = useState<UserDTO[]>([]);
  const [userMatch, setUserMatch] = useState<WIRUser>({});

  const allWirUsers = useWIRUsers();
  const { usersLoading, fetchUsersList } = useFetchWgUsersList();

  const allWirUsersFiltered = useMemo(
    () =>
      cloneDeep(allWirUsers)
        .filter(item =>
          assetTypeFilter !== ALL_WIR
            ? item.assets?.includes(assetTypeFilter as AssetType) ||
              item.assets?.includes(ALL_WIR as AssetType)
            : item,
        )
        .filter(item =>
          roleTypeFilter !== NOT_APPLICABLE
            /* istanbul ignore next */
            ? item.roles?.includes(roleTypeFilter as WIRTransverseRoles)
            : item,
        )
        .filter(item =>
          /* istanbul ignore next */
          searchTypeFilter !== 'N/A' ? item.emailAddress === searchTypeFilter : item,
        )
        /* istanbul ignore next */
        .sort((itemA, itemB) => (itemA.userName ?? '').localeCompare(itemB.userName ?? '')),
    [allWirUsers, assetTypeFilter, roleTypeFilter, searchTypeFilter],
  );

  useEffect(() => setPage(1), [allWirUsersFiltered]);

  useEffect(() => {
    if (page) {
      setWirUsersForPage([]);
      const itemStart = (page - 1) * WIR_USERS_PAGE_SIZE;
      const itemEnd = itemStart + WIR_USERS_PAGE_SIZE;
      const wirUserForCurrentPage = allWirUsersFiltered.slice(itemStart, itemEnd);
      setWirUsersForPage(wirUserForCurrentPage);
    }
  }, [page, allWirUsersFiltered]);

  const getPageSize = () => {
    return Math.ceil(allWirUsersFiltered.length / WIR_USERS_PAGE_SIZE);
  };

  useEffect(() => {
    const fetchUserData = async () => {
      const response = await userApiClient.getUsers();

      setUserData(response);
    };

    fetchUserData();
  }, []);

  useEffect(() => {
    if (user.emailAddress) {
      const filteredUser = userData?.find(
        u => u.email?.toLowerCase() === user.emailAddress?.toLowerCase(),
      );
      /* istanbul ignore next */
      if (filteredUser) {
        setUserMatch({
          ...user,
          userId: filteredUser.igg,
          userName: filteredUser.firstName + ' ' + filteredUser.lastName,
        });
      }
    }
  }, [setUser, user, user.emailAddress, userData]);

  const handleUserResponse = useCallback(
    async (user: WIRUser, createUserBody: Record<string, any>) => {
      const userResponse =
        mode === 'add'
          ? await wellGuardianApiClient.createWIRUser(user, countryData)
          /* istanbul ignore next */
          : await wellGuardianApiClient.editWIRUser(user, countryData);
      if (userResponse?.userId) {
        toast.success(
          intl?.formatMessage({
            id: 'wellGuardian.wir.userRoleSettings.wgUserSavedSuccessfully',
          }),
          {
            hideProgressBar: false,
            theme: 'colored',
          },
        );
        const res = await wellGuardianApiClient.createWGUser(createUserBody);
        /* istanbul ignore next */
        if (res && 'igg' in res && res.igg === user.userId) {
          toast.success(
            intl?.formatMessage({
              id: 'wellGuardian.wir.userRoleSettings.userSavedSuccessfully',
            }),
            {
              hideProgressBar: false,
              theme: 'colored',
            },
          );
        } else {
          /* istanbul ignore next */
          if (
            res &&
            /* istanbul ignore next */
            'response' in res &&
            /* istanbul ignore next */
            res?.response?.body?.message !== WG_USER_EXISTS_ERROR_MESSAGE
          ) {
            toast.error(
              intl?.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.errorSavingUser' }),
              {
                hideProgressBar: false,
                theme: 'colored',
              },
            );
          }
        }
      /* istanbul ignore next */
      } else {
        toast.error(
          intl?.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.errorSavingUser' }),
          {
            hideProgressBar: false,
            theme: 'colored',
          },
        );
      }
    },
    [mode, countryData, intl],
  );

  const getLastName = (email: string | undefined) => {
    if (email === undefined) {
      return '';
    }
    const [emailFirstPart] = email.split('@');

    return emailFirstPart?.split('.').map(capitalizeName)[1];
  };

  const saverUser = useCallback(async () => {
    const createUserBody = {
      igg: user.userId,
      email: user.emailAddress,
      firstName: user.userName?.split(' ')[0],
      lastName: user.userName?.split(' ')[1] || getLastName(user?.emailAddress),
      profile: Profiles.INTERNAL,
      unitSystem: UnitSystems.METRIC,
      wellIds: [],
      tuwis: [],
      wellGroups: [],
      isBeta: false,
      tightWellAccessible: false,
    };
    const body: Record<string, string | number> = {};
    (Object.keys(createUserBody) as (keyof UserDTO)[]).forEach(key => {
      body[key.toUpperCase()] = (createUserBody as Record<string, any>)[key];
    });
    // : await userApiClient.updateUser(user.userId!, body);
    if (mode === 'add') {
      if (userMatch.emailAddress === user.emailAddress) {
        await handleUserResponse(user, createUserBody);
      } else {
        await handleUserResponse(user, createUserBody);
      }
      // else {
      //   const response = await userApiClient.createUser(createUserBody);
      //   if (response?.igg === user.userId) {
      //     toast.success(
      //       intl?.formatMessage({
      //         id: 'wellGuardian.wir.userRoleSettings.userSavedSuccessfullySO',
      //       }),
      //     );
      //   } else {
      //     toast.error(
      //       intl?.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.errorSavingUserSO' }),
      //     );
      //   }
      // }
    }
    if (mode !== 'add') {
      const userResponse = await wellGuardianApiClient.editWIRUser(user, countryData);
      /* istanbul ignore next */
      if (userResponse?.userId) {
        toast.success(
          intl?.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.userSavedSuccessfully' }),
          {
            hideProgressBar: false,
            theme: 'colored',
          },
        );
      } else {
        toast.error(
          intl?.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.errorSavingUser' }),
          {
            hideProgressBar: false,
            theme: 'colored',
          },
        );
      }
    }
    setOpenUserDetailsModal(false);
    fetchUsersList();
  }, [user, mode, fetchUsersList, userMatch.emailAddress, handleUserResponse, countryData, intl]);

  const okAPIMethod = useCallback(async () => {
    const userResponse =
      excelData &&
      /* istanbul ignore next */
      multiUser &&
      /* istanbul ignore next */
      (await wellGuardianApiClient.createMultipleWIRUser(finalExcelData, countryData));
    /* istanbul ignore next */
    if (userResponse && userResponse.length > 0) {
      toast.success(
        intl?.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.userSavedSuccessfully' }),
        {
          hideProgressBar: false,
          theme: 'colored',
        },
      );

      const createUserBodies = finalExcelData.map((data: any) => ({
        igg: data.userId,
        firstName: data.userName?.split(' ')[0],
        lastName:
          data.userName?.split(' ')[1] === undefined
            /* istanbul ignore next */
            ? data.userName?.split(' ')[0]
            /* istanbul ignore next */
            : data.userName?.split(' ')[1],
        email: data.emailAddress,
        isBeta: false,
        tightWellAccessible: 'false',
        profile: Profiles.INTERNAL,
      }));

      const response = await wellGuardianApiClient.createUserBatch(createUserBodies, countryData);

      /* istanbul ignore next */
      if (response !== null) {
        toast.success(
          intl?.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.userSavedSuccessfully' }),
          {
            hideProgressBar: false,
            theme: 'colored',
          },
        );
      /* istanbul ignore next */
      } else {
        toast.error(
          intl?.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.errorSavingUser' }),
          {
            hideProgressBar: false,
            theme: 'colored',
          },
        );
      }
    } else {
      toast.error(
        intl?.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.errorSavingUser' }),
        {
          hideProgressBar: false,
          theme: 'colored',
        },
      );
    }
    setOpenUserDetailsModal(false);
    fetchUsersList();
    setMultiUser(false);
    setExcelData(null);
  }, [countryData, excelData, fetchUsersList, finalExcelData, intl, multiUser]);

  const deleteUser = useCallback(async () => {
    if (user.id) {
      const userResponse = user && (await wellGuardianApiClient.deleteWIRUser(user.id));
      if (userResponse) {
        // await wellGuardianApiClient.deleteWGUser(user.userId!);
        toast.success(
          intl?.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.userRemovedSuccessfully' }),
          {
            hideProgressBar: false,
            theme: 'colored',
          },
        );
      /* istanbul ignore next */
      } else {
        toast.error(
          toast.error(
            intl?.formatMessage({ id: 'wellGuardian.wir.userRoleSettings.errorRemovingUser' }),
          ),
          {
            hideProgressBar: false,
            theme: 'colored',
          },
        );
      }
    }
    setOpenRemoveUserConfirmationModal(false);
    fetchUsersList();
  }, [user, fetchUsersList, intl]);

  return {
    openUserRoleSettingsModal,
    setOpenUserRoleSettingsModal,
    wirUsersForPage,
    page,
    setPage,
    user,
    setUser,
    mode,
    setMode,
    openUserDetailsModal,
    setOpenUserDetailsModal,
    openRemoveUserConfirmationModal,
    setOpenRemoveUserConfirmationModal,
    saverUser,
    deleteUser,
    usersLoading,
    assetTypeFilter,
    setAssetTypeFilter,
    roleTypeFilter,
    setRoleTypeFilter,
    setSearchTypeFilter,
    allWirUsers: allWirUsersFiltered,
    pageSize: getPageSize(),
    okAPIMethod,
    excelData,
    setExcelData,
    multiUser,
    setMultiUser,
    finalExcelData,
    setFinalExcelData,
  };
};
